import { HttpClient, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { BehaviorSubject, catchError, filter, Observable, switchMap, take, throwError } from "rxjs";
import { UiMessageService } from "./ui-message-broker/ui-message-broker.service";

@Injectable({ providedIn: 'root' })
export class AppInterceptorService implements HttpInterceptor {

  private refreshInProgress = false;
  private refreshSubject = new BehaviorSubject<boolean | null>(null);

  private uiMessageService : UiMessageService = inject(UiMessageService);

  constructor(private http: HttpClient) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    // Callers can opt out of the refresh/reload cycle (e.g. actuator probes
    // on downstream services that 401 legitimately) by setting this header.
    const skipAuthRefresh = req.headers.get('X-Skip-Auth-Refresh') === 'true';
    let headers = req.headers.delete('X-Skip-Auth-Refresh');

    // Always attach the UI session id so every backend service can put it in
    // its SLF4J MDC (nucleus-core's UiSessionMdcFilter picks it up and sets
    // the "uiSessionId" MDC key). Lets operators filter logs across services
    // for a single browser tab. Don't overwrite an explicit value — some
    // callers (e.g. monitoring subscriptions) still build the header
    // themselves and we want to keep that pathway intact.
    if (!headers.has('X-Ui-Session-Id')) {
      headers = headers.set('X-Ui-Session-Id', AppInterceptorService.getOrCreateSessionId());
    }

    const request = req.clone({ headers, withCredentials: true });

    return next.handle(request).pipe(
      catchError(err => {

        // NUC-86: gateway forced-rejection codes — JWT/user is permanently
        // dead, refresh would loop. Go straight to login. Match against the
        // structured-error envelope { code, message } returned by
        // ReverseProxyFilter on blacklist hits.
        const code = err?.error?.code;
        if (code === 'TOKEN_REVOKED' || code === 'USER_BLOCKED' || code === 'IP_BLOCKED') {
          window.location.assign('/oauth2/authenticate');
          return throwError(() => err);
        }

        // Not auth-related → propagate. Some Spring Security configs return
        // 403 (instead of 401) for an expired access token, so we treat both
        // as recoverable. Authority-denied 403s will also try a refresh and
        // then propagate the second 403 — one wasted refresh per legitimate
        // permission denial, which is the acceptable cost for catching the
        // expired-token case uniformly.
        if (err.status !== 401 && err.status !== 403) {
          return throwError(() => err);
        }

        // 🚫 Caller opted out of the refresh dance
        if (skipAuthRefresh) {
          return throwError(() => err);
        }

        // 🚫 Never refresh for auth/session endpoints
        if (this.isAuthEndpoint(req.url)) {
          return throwError(() => err);
        }

        // 🔄 Refresh already running → wait for its OUTCOME, success or failure.
        //
        // NUC-252: this filtered on `v === true` and therefore only ever woke on
        // SUCCESS. When a refresh FAILED nothing pushed a further value, so every
        // request parked here stayed subscribed to a subject that would never emit
        // again: the observable never emitted, never errored and never completed, so
        // handleRequest never reached onComplete and the blocking overlay never hid.
        // The app looked frozen. That is the ordinary case - several parallel XHRs on
        // page load when the token has expired - not a corner case.
        //
        // `v !== null` wakes on both outcomes; the switchMap below decides which.
        if (this.refreshInProgress) {
          return this.refreshSubject.pipe(
            filter(v => v !== null),
            take(1),
            switchMap(ok => ok
              // refresh succeeded → replay, re-cloning so the new cookie is read
              ? next.handle(req.clone({ withCredentials: true }))
              // refresh failed → surface the ORIGINAL error, do not replay
              : throwError(() => err))
          );
        }

        // 🔑 Start refresh
        this.refreshInProgress = true;
        this.refreshSubject.next(null);

        return this.refreshToken().pipe(
          switchMap(() => {
            this.refreshInProgress = false;
            this.refreshSubject.next(true);
            this.uiMessageService.reconnect();
            // 🔁 MUST re-clone so cookies are re-read
            return next.handle(req.clone({ withCredentials: true }));
          }),
          catchError(refreshErr => {
            this.refreshInProgress = false;
            // NUC-252: RELEASE THE WAITERS. Without this every request parked in the
            // block above hangs forever - see the comment there. false (not null)
            // because null is the "refresh in flight" state the waiters filter out.
            this.refreshSubject.next(false);
            // Don't redirect to /oauth2/authenticate on refresh failure.
            // If refresh persistently fails the redirect kicks off a new
            // OAuth round-trip that lands back on the same failing endpoint
            // — observed 400+ stuck XHRs in the browser as the loop
            // accumulated, exhausting Apache MaxRequestWorkers upstream.
            // Surface the error; user can manually retry once backend
            // recovers.
            return throwError(() => refreshErr);
          })
        );
      })
    );
  }

  private refreshToken(): Observable<any> {
    return this.http.post(
      '/oauth2/refreshtoken',
      new URLSearchParams({ grant_type: 'refresh_token' }),
      { withCredentials: true }
    );
  }

  private isAuthEndpoint(url: string): boolean {
    return (
      url.includes('/auth/me') ||
      url.includes('/oauth2/refreshtoken') ||
      url.includes('/oauth2/logout') ||
      url.includes('/oauth2/authorize') ||
      url.includes('/login')
    );
  }

  /** Stable per-browser id persisted in localStorage. Generated on first
   *  outgoing request if the login flow hasn't populated one yet. Kept on a
   *  class static so every consumer (interceptor, monitoring service, any
   *  future diagnostic hook) reads/writes the same slot. */
  static getOrCreateSessionId(): string {
    let sid = localStorage.getItem('uiSessionId');
    if (!sid) {
      sid = 'ui-' + Math.random().toString(36).slice(2, 10) + '-' + Date.now();
      localStorage.setItem('uiSessionId', sid);
    }
    return sid;
  }
}